import { useMemo, useState } from 'react';
import { FileSpreadsheet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemGroup,
  ItemTitle,
} from '@/components/ui/item';
import { useResourceSets } from 'components/IAP/hooks/resourceSetHooks';
import { AssetImportDialog } from 'components/MapPage/CorporateViewTabs/ResourceTab/AssetImportDialog';
import { useAors } from 'hooks/useAors';
import { useAorText } from 'utils/aorTerminology';
import { assetCountsByAor } from '../onboardingModel';

export function AssetStep() {
  const aorText = useAorText();
  const [importOpen, setImportOpen] = useState(false);
  const { data: aors = [] } = useAors();
  const { data: assets = [] } = useResourceSets();
  const summary = useMemo(() => assetCountsByAor(aors, assets), [aors, assets]);

  return (
    <div className="tw:flex tw:flex-col tw:gap-3">
      <div>
        <h3 className="tw:m-0 tw:text-sm tw:font-semibold">
          Import resources and assets
        </h3>
        <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
          Upload a spreadsheet of facilities, sites, or equipment. Rules can
          then open events when a hazard comes near them.
        </p>
      </div>
      <div>
        <Button type="button" onClick={() => setImportOpen(true)}>
          <FileSpreadsheet data-icon="inline-start" />
          Import spreadsheet
        </Button>
      </div>

      <p className="tw:m-0 tw:text-sm">
        {assets.length === 0
          ? 'No assets yet.'
          : `${assets.length} asset${assets.length === 1 ? '' : 's'} in your organization.`}
      </p>
      {assets.length > 0 && summary.perAor.length > 0 ? (
        <ItemGroup className="tw:gap-1">
          {summary.perAor.map((entry) => (
            <Item key={entry.aorId} variant="outline" size="xs">
              <ItemContent>
                <ItemTitle>{entry.name}</ItemTitle>
              </ItemContent>
              <ItemActions className="tw:text-muted-foreground">
                {entry.count}
              </ItemActions>
            </Item>
          ))}
          {summary.outside > 0 ? (
            <Item variant="muted" size="xs">
              <ItemContent>
                <ItemTitle>{aorText('Outside every AOR')}</ItemTitle>
              </ItemContent>
              <ItemActions className="tw:text-muted-foreground">
                {summary.outside}
              </ItemActions>
            </Item>
          ) : null}
        </ItemGroup>
      ) : null}
      {summary.unlocated > 0 ? (
        <p className="tw:m-0 tw:text-xs tw:text-amber-400">
          {summary.unlocated} asset{summary.unlocated === 1 ? ' has' : 's have'}{' '}
          no location, so no rule can match{' '}
          {summary.unlocated === 1 ? 'it' : 'them'}. Fix the address or
          coordinates in the Assets tab.
        </p>
      ) : null}

      <AssetImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={() => setImportOpen(false)}
      />
    </div>
  );
}
